import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  getBottomBoundary,
  getLeftBoundary,
  getRightBoundary,
  getShapeChromeScreenRect,
  getTopBoundary,
} from '@/lib/editor/floating-canvas-chrome-bounds'
import {
  CROP_TOOLBAR_GAP,
  CROP_TOOLBAR_HEIGHT,
  resolveCropToolbarPlacement,
} from '@/components/app/editor/tools/crop-toolbar'

import { PANEL_HEIGHT, PANEL_WIDTH } from '../constants/zoom-slider-dimensions'

const GAP = 20

export function computeZoomSliderPos(
  shape: ShapeData,
  scale: number,
  canvasRect: DOMRect
): { x: number; y: number } {
  const { left, right, top, bottom } = getShapeChromeScreenRect(shape, scale, canvasRect)
  const cx = (left + right) / 2
  const topBoundary = getTopBoundary()
  const bottomBoundary = getBottomBoundary()
  const leftBoundary = getLeftBoundary()
  const rightBoundary = getRightBoundary()
  const clampX = (v: number) =>
    Math.max(leftBoundary + 8, Math.min(rightBoundary - PANEL_WIDTH - 8, v))
  const clampY = (v: number) =>
    Math.max(topBoundary, Math.min(bottomBoundary - PANEL_HEIGHT - 8, v))

  // The crop toolbar defaults to the same "just below the shape" position this slider does — if
  // it can't fit ABOVE the shape (e.g. the shape sits near the top of the page) it falls back to
  // below, which would otherwise land right on top of this slider. When that happens, stack this
  // slider beneath the toolbar instead of beneath the shape.
  const toolbarPlacement = resolveCropToolbarPlacement(shape, scale, canvasRect)
  const belowShapeY =
    bottom + GAP + (toolbarPlacement === 'below' ? CROP_TOOLBAR_HEIGHT + CROP_TOOLBAR_GAP : 0)
  const fitsBelow = belowShapeY + PANEL_HEIGHT + 8 <= bottomBoundary

  // Mirror image of the above fix: when the shape sits near the BOTTOM of the page, there's no
  // room left below it (or below the toolbar, if the toolbar landed there) for this slider either.
  // In that case the toolbar has room above the shape instead — stack the slider above the
  // toolbar rather than clamping it down into an overlap with the shape/toolbar.
  const y =
    !fitsBelow && toolbarPlacement === 'above'
      ? top - CROP_TOOLBAR_GAP - CROP_TOOLBAR_HEIGHT - GAP - PANEL_HEIGHT
      : belowShapeY

  return { x: clampX(cx - PANEL_WIDTH / 2), y: clampY(y) }
}
